/**
 * Name: Edit Edit Playlist Server
 * Author: @Gaurav
 * Date: 20-07-2021
 */
import React, { useState } from 'react';
import Sidebar from '../../components/SideBar';
import NavBar from '../../components/NavBarSearchSong';
import Settings from '../../assets/Settings.png';
import search from '../../assets/ic_search.png';
import RoundButton from '../../components/RoundButton';
import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { formatDateToDisplay } from '../../utils';
import AlertModal from '../../components/Modal';
import SortIndicator from '../../components/SortIndicator';
import PlaylistActionIcon from '../../components/PlaylistManagement/PlaylistActionIcon';
import RangeSelectionButton from '../../components/RangeSelectionButton';
import useRangeSelection, { selectionShouldCheck } from '../../utils/useRangeSelection';

/**
 * material table style
 */
const useStyles = makeStyles({
  table: {
    minWidth: 372,
    borderRadius: 30,
  },
});

function EditPlaylistServer({
  onPlaylistTabClick,
  onUpdatePlaylist,
  selectedSongsToUpload,
  selectedCurrentSongIds,
  handleSelectCurrentSongs,
  handleRemoveSelectedSongs,
  handleSelectSongsToUpload,
  handleMultipleSongsToUpload,
  handleRemoveSong,
  handlePlaySong,
  handleSearchInput,
  searchInput,
  songsList,
  isPageOpenedAsPopup,
}) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [navbarCurrentSong, setNavbarCurrentSong] = useState(null);
  const { choose: chooseRange, toggleRangeMode, resetRange, isRangeMode } = useRangeSelection();

  // Sorting states
  const [songsSortConfig, setSongsSortConfig] = useState({ key: null, direction: 'desc' });
  const [selectedSortConfig, setSelectedSortConfig] = useState({ key: null, direction: 'desc' });

  /**
   * Close Modal
   */
  const handleClose = () => {
    setOpen(false);
  };

  const handleRemoveSongLocal = (deleteSelectedPlaylist) => {
    if (Array.isArray(deleteSelectedPlaylist)) handleRemoveSelectedSongs();
    else handleRemoveSong(deleteSelectedPlaylist);
    setOpen(false);
  };

  const handleSort = (key, sortConfig, setSortConfig) => {
    let direction = 'desc';
    let newKey = key;

    if (sortConfig.key === key) {
      if (sortConfig.direction === 'desc') {
        direction = 'asc';
      } else {
        newKey = null;
      }
    }

    setSortConfig({ key: newKey, direction });
  };

  const sortData = (data, sortConfig) => {
    if (!sortConfig.key || !Array.isArray(data)) return data;

    return [...data].sort((a, b) => {
      if (sortConfig.key === 'createdAt') {
        const valueA = new Date(a.createdAt).getTime();
        const valueB = new Date(b.createdAt).getTime();
        return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
      }

      const valueA = (a[sortConfig.key] || a.title || a.fileName || a.name || '').toString();
      const valueB = (b[sortConfig.key] || b.title || b.fileName || b.name || '').toString();

      return sortConfig.direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  };

  const isAllSongsSelected = () => {
    if (!songsList || !songsList.length) return false;
    return songsList.every((song) => selectedSongsToUpload.some((item) => item.id === song.id));
  };

  // Find current song index in the list
  const getCurrentSongIndex = () => {
    if (!navbarCurrentSong || !Array.isArray(songsList)) return -1;
    return songsList.findIndex(song => song.id === navbarCurrentSong.id);
  };

  // Check if there's a next song
  const hasNextSong = () => {
    const currentIndex = getCurrentSongIndex();
    return currentIndex >= 0 && currentIndex < songsList.length - 1;
  };

  // Check if there's a previous song
  const hasPrevSong = () => {
    const currentIndex = getCurrentSongIndex();
    return currentIndex > 0;
  };

  // Handle playing next song
  const handleNextSong = () => {
    const currentIndex = getCurrentSongIndex();
    if (currentIndex >= 0 && currentIndex < songsList.length - 1) {
      const nextSong = songsList[currentIndex + 1];
      setNavbarCurrentSong({
        ...nextSong,
        name: nextSong.title,
      });
    }
  };

  // Handle playing previous song
  const handlePrevSong = () => {
    const currentIndex = getCurrentSongIndex();
    if (currentIndex > 0) {
      const prevSong = songsList[currentIndex - 1];
      setNavbarCurrentSong({
        ...prevSong,
        name: prevSong.title,
      });
    }
  };

  // Handle playing song from row (updates navbar player only)
  const handlePlaySongFromRow = (song) => {
    setNavbarCurrentSong({
      ...song,
      name: song.title || song.name || song.fileName || 'Unnamed song',
    });
  };

  // Handle closing the player
  const handleClosePlayer = () => {
    setNavbarCurrentSong(null);
  };

  const isNavbarSongAddedToSelected =
    !!navbarCurrentSong && selectedSongsToUpload.some((item) => item.id === navbarCurrentSong.id);

  const handleToggleNavbarSongInSelected = (song) => {
    if (!song?.id) return;
    const currentlyAdded = selectedSongsToUpload.some((item) => item.id === song.id);
    handleSelectSongsToUpload({ target: { checked: !currentlyAdded } }, song);
  };

  return (
    <div className={`main ${isPageOpenedAsPopup ? 'edit-playlist-popup' : ''}`}>
      {!isPageOpenedAsPopup && (
        <div className="sidebar">
          <Sidebar />
        </div>
      )}
      <AlertModal
        open={open}
        onClose={handleClose}
        handelDelete={() => handleRemoveSongLocal(selectedPlaylist)}
        description={Array.isArray(selectedPlaylist)
          ? `Remove ${selectedPlaylist.length} selected songs from this playlist? Save to apply the change.`
          : 'Remove this song from the playlist? Save to apply the change.'}
      />
      <div className="leftPortion">
        <NavBar
          data="Edit Playlist"
          icon={Settings}
          currentRouteName="Playlist"
          breadcrumbsPath="Edit Playlist / Server"
          showPlayer={true}
          currentSong={navbarCurrentSong}
          onNextSong={handleNextSong}
          hasNextSong={hasNextSong()}
          onPrevSong={handlePrevSong}
          hasPrevSong={hasPrevSong()}
          onClosePlayer={handleClosePlayer}
          showAddToPlaylist={true}
          isCurrentSongAddedToPlaylist={isNavbarSongAddedToSelected}
          onToggleAddToPlaylist={handleToggleNavbarSongInSelected}
        />
        <div className="content">
          <div className="warp_edit_row">
            <div className="editRow">
              <div className="wrap_edit">
                <p>Edit:</p>
                <RoundButton className="disabled-button" handleClick={() => onPlaylistTabClick('playlistTabs')} btnText="Playlist" />
                <RoundButton btnText="server" />
              </div>
              <div className="search_row wdth">
                <input
                  className="input_field"
                  placeholder="Search something here..."
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchInput(e.target.value)}
                />
                <img src={search} alt="search" />
              </div>
            </div>
            <div className="">
              <RoundButton
                btnText={`Remove selected (${selectedCurrentSongIds.length})`}
                disabled={!selectedCurrentSongIds.length}
                handleClick={() => { setSelectedPlaylist(selectedSongsToUpload.filter((song) => selectedCurrentSongIds.includes(song.id))); setOpen(true); }}
              />
              <RoundButton handleClick={onUpdatePlaylist} btnText="Save" />
            </div>
          </div>

          <div className="main_tbl_wrap">
            <TableContainer className="tbl_wraper tbl_wrap" style={{ border: '1px solid #40189D' }} component={Paper}>
              <Table className={classes.table} stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ position: 'sticky', left: 0, zIndex: 4, backgroundColor: '#fafafa' }}>
                      <Checkbox
                        inputProps={{ 'aria-label': 'uncontrolled-checkbox' }}
                        onChange={(event) => {
                          resetRange('server-songs');
                          handleMultipleSongsToUpload(event, songsList);
                        }}
                        checked={isAllSongsSelected()}
                      />
                      <RangeSelectionButton active={isRangeMode('server-songs')} onClick={() => toggleRangeMode('server-songs')} />
                    </TableCell>
                    <TableCell className="table_font">Result</TableCell>
                    <TableCell 
                      onClick={() => handleSort('title', songsSortConfig, setSongsSortConfig)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Song Name<SortIndicator sortConfig={songsSortConfig} columnKey="title" />
                    </TableCell>
                    <TableCell 
                      onClick={() => handleSort('artist', songsSortConfig, setSongsSortConfig)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Artist<SortIndicator sortConfig={songsSortConfig} columnKey="artist" />
                    </TableCell>
                    <TableCell 
                      onClick={() => handleSort('createdAt', songsSortConfig, setSongsSortConfig)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Date<SortIndicator sortConfig={songsSortConfig} columnKey="createdAt" />
                    </TableCell>
                    <TableCell style={{ position: 'sticky', right: 0, zIndex: 4, backgroundColor: '#fafafa' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(songsList) &&
                    sortData(songsList, songsSortConfig).map((row, index) => (
                      <TableRow key={row.id}>
                        <TableCell style={{ position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#ffffff' }}>
                          <Checkbox
                            onChange={(event) => {
                              const selection = chooseRange('server-songs', sortData(songsList, songsSortConfig), row, event.target.checked, !!event.nativeEvent?.shiftKey);
                              handleMultipleSongsToUpload({ target: { checked: selectionShouldCheck(selectedSongsToUpload.map((item) => item.id), selection) } }, selection.items);
                            }}
                            inputProps={{ 'aria-label': 'uncontrolled-checkbox' }}
                            name="song"
                            checked={selectedSongsToUpload.some((item) => item.id === row.id)}
                          />
                        </TableCell>
                        <TableCell className="table-index-cell">#{++index}</TableCell>
                        <TableCell className="table-name-cell">{row.title}</TableCell>
                        <TableCell className="table-name-cell">{row.artist || 'N/A'}</TableCell>
                        <TableCell>{formatDateToDisplay(row.createdAt)}</TableCell>
                        <TableCell style={{ position: 'sticky', right: 0, zIndex: 3, backgroundColor: '#ffffff' }}>
                          <span
                            onClick={() => handlePlaySongFromRow(row)}
                            style={{
                              cursor: 'pointer',
                            }}
                          >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17317C0.00433284 8.00043 -0.193701 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C19.9971 7.34873 18.9426 4.80688 17.0679 2.93215C15.1931 1.05742 12.6513 0.0029116 10 0ZM10 18C8.41775 18 6.87104 17.5308 5.55544 16.6518C4.23985 15.7727 3.21447 14.5233 2.60897 13.0615C2.00347 11.5997 1.84504 9.99113 2.15372 8.43928C2.4624 6.88743 3.22433 5.46197 4.34315 4.34315C5.46197 3.22433 6.88743 2.4624 8.43928 2.15372C9.99113 1.84504 11.5997 2.00346 13.0615 2.60896C14.5233 3.21447 15.7727 4.23984 16.6518 5.55544C17.5308 6.87103 18 8.41775 18 10C17.9976 12.121 17.154 14.1544 15.6542 15.6542C14.1545 17.154 12.121 17.9976 10 18Z"
                                fill="#40189D"
                              />
                              <path d="M7 4.446V15.554L15.887 10L7 4.446ZM9 8.054L12.113 10L9 11.946V8.054Z" fill="#40189D" />
                            </svg>
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer className="tbl_wraper selectTbl" component={Paper}>
              <Table className={classes.table} stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        checked={selectedSongsToUpload.length > 0 && selectedSongsToUpload.every((song) => selectedCurrentSongIds.includes(song.id))}
                        indeterminate={selectedCurrentSongIds.length > 0 && selectedCurrentSongIds.length < selectedSongsToUpload.length}
                        onChange={(event) => {
                          resetRange('selected-songs');
                          handleSelectCurrentSongs(selectedSongsToUpload, event.target.checked);
                        }}
                        inputProps={{ 'aria-label': 'Select all songs in this playlist' }}
                      />
                      <RangeSelectionButton active={isRangeMode('selected-songs')} onClick={() => toggleRangeMode('selected-songs')} />
                    </TableCell>
                    <TableCell className="table_font">Result</TableCell>
                    <TableCell 
                      onClick={() => handleSort('title', selectedSortConfig, setSelectedSortConfig)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Selected Songs<SortIndicator sortConfig={selectedSortConfig} columnKey="title" />
                    </TableCell>
                    <TableCell 
                      onClick={() => handleSort('artist', selectedSortConfig, setSelectedSortConfig)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      Artist<SortIndicator sortConfig={selectedSortConfig} columnKey="artist" />
                    </TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortData(selectedSongsToUpload, selectedSortConfig).map((row, index) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedCurrentSongIds.includes(row.id)}
                          onChange={(event) => {
                            const selection = chooseRange('selected-songs', sortData(selectedSongsToUpload, selectedSortConfig), row, event.target.checked, !!event.nativeEvent?.shiftKey);
                            handleSelectCurrentSongs(selection.items, selectionShouldCheck(selectedCurrentSongIds, selection));
                          }}
                          inputProps={{ 'aria-label': `Select ${row.title || row.name || row.fileName || 'song'}` }}
                        />
                      </TableCell>
                      <TableCell className="table-index-cell">#{++index}</TableCell>
                      <TableCell className="table-name-cell">{row.title || row.fileName || row.name || 'N/A'}</TableCell>
                      <TableCell className="table-name-cell">{row.artist || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="playlist-management-actions">
                          <PlaylistActionIcon kind="listen" label={`Listen to ${row.title || row.name || row.fileName || 'song'}`} onClick={() => handlePlaySongFromRow(row)} />
                          <PlaylistActionIcon kind="remove" label={`Remove ${row.title || row.name || row.fileName || 'song'} from this playlist`} onClick={() => { setOpen(true); setSelectedPlaylist(row); }} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
export default EditPlaylistServer;
